import { useTranslation } from 'react-i18next'
import { AuroraHeatPumpMode } from '../enums'
import { MouseEvent } from 'react'
import { Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { WarningAmber } from '@mui/icons-material'
import AuroraControl from './AuroraControl'

const OTHER_MODE_LABEL_KEYS: Partial<Record<AuroraHeatPumpMode, string>> = {
  [AuroraHeatPumpMode.HOT_WATER]: 'hotWater',
  [AuroraHeatPumpMode.FREEZE_STAT]: 'freezeStat',
  [AuroraHeatPumpMode.LEGIONELLA]: 'legionella',
  [AuroraHeatPumpMode.HEATING_ECO]: 'heatingEco'
}

export interface AuroraHeatPumpModeControlProps {
  value: AuroraHeatPumpMode
  loading?: boolean
  disabled?: boolean
  onSubmit: (mode: AuroraHeatPumpMode) => Promise<void>
}

export default function AuroraHeatPumpModeControl({
  value,
  loading = false,
  disabled = false,
  onSubmit
}: AuroraHeatPumpModeControlProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * Handle the change event.
   */
  async function handleChange(_: MouseEvent<HTMLElement>, newValue: AuroraHeatPumpMode) {
    try {
      await onSubmit(newValue)
    } catch {
      // Ignore the error.
    }
  }

  const otherModeLabelKey = OTHER_MODE_LABEL_KEYS[value]

  return (
    <AuroraControl
      direction="column"
      label={t('heatPumpMode')}
      loading={loading}
      disabled={disabled}
      control={
        <Stack direction="column" alignItems="center" gap={1}>
          <ToggleButtonGroup
            color="primary"
            value={value}
            sx={{ mt: 2 }}
            exclusive
            disabled={disabled}
            onChange={handleChange}
          >
            <ToggleButton value={AuroraHeatPumpMode.OFF}>{t('off')}</ToggleButton>
            <ToggleButton value={AuroraHeatPumpMode.COOLING}>{t('cooling')}</ToggleButton>
            <ToggleButton value={AuroraHeatPumpMode.HEATING}>{t('heating')}</ToggleButton>
          </ToggleButtonGroup>
          {otherModeLabelKey && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <WarningAmber fontSize="small" color="warning" />
              <Typography variant="body2" color="text.secondary">
                {t(otherModeLabelKey)}
              </Typography>
            </Stack>
          )}
        </Stack>
      }
    />
  )
}
